import { fetchApiJson, trackError, trackUserEvent } from '@vend/utilities'
import { PaymentRequestDto } from '../../models/invoicing-payment-request'

type CreatePaymentRequestResponse = {
  success: boolean
  paymentRequestId?: string
  error?: string
}

/**
 * Validates payment request metadata fields required for payment reconciliation
 *
 * @param paymentRequestData {PaymentRequestDto} The payment request data to validate
 * @return {CreatePaymentRequestResponse | null} Error response if validation fails, null if valid
 */
const validatePaymentRequestMetadata = (
  paymentRequestData: PaymentRequestDto
): CreatePaymentRequestResponse | null => {
  if (!paymentRequestData.metadata) {
    return {
      success: false,
      error:
        'Payment request metadata is required. Please ensure LSPAY payment type is configured.',
    }
  }

  const {
    retailerId,
    registerId,
    retailerPaymentTypeId,
    documentRef,
  } = paymentRequestData.metadata

  if (!retailerPaymentTypeId) {
    return {
      success: false,
      error:
        'LSPAY payment type is required to create a payment request. Please ensure LSPAY is configured.',
    }
  }

  if (!retailerId) {
    return {
      success: false,
      error: 'Retailer ID is required to create a payment request.',
    }
  }

  if (!registerId) {
    return {
      success: false,
      error: 'Register ID is required to create a payment request.',
    }
  }

  if (!documentRef) {
    return {
      success: false,
      error: 'Document reference is required to create a payment request.',
    }
  }

  return null
}

/**
 * Create a payment request via the invoicing proxy
 *
 * @param paymentRequestData {PaymentRequestDto} The payment request data to send
 * @return {Promise<CreatePaymentRequestResponse>} A promise resolved when payment request has been created
 */
export const createPaymentRequest = async (
  paymentRequestData: PaymentRequestDto
): Promise<CreatePaymentRequestResponse> => {
  const validationError = validatePaymentRequestMetadata(paymentRequestData)
  if (validationError) {
    return validationError
  }

  paymentRequestData.currency = paymentRequestData.pricingTables?.[0]?.currency
  paymentRequestData.pli = `lsx:${paymentRequestData.location.locationRef}`
  paymentRequestData.customer.creditAccountRef =
    paymentRequestData.customer.customerRef // There is no creditAccountRef in Xseries, will use customerRef for now
  paymentRequestData.requestedAmount = Math.round(
    Number(paymentRequestData.requestedAmount || 0) * 100
  )

  try {
    const response = await fetchApiJson('/api/2.0/invoicing/payment-requests', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(paymentRequestData),
    })

    // Track successful payment request creation
    trackUserEvent('Web Register', 'Payment Request Created', {
      customerRef: paymentRequestData.customer.customerRef,
      requestedAmount: paymentRequestData.requestedAmount,
      email: paymentRequestData.sendToEmail,
      shopRef: paymentRequestData.shopRef,
    })

    return {
      success: true,
      paymentRequestId: response.id,
    }
  } catch (err: any) {
    // Track and log the error
    const errorMessage = err?.message || 'Unknown error occurred'
    trackError('Payment Request Creation Error', errorMessage, {
      customerRef: paymentRequestData.customer.customerRef,
      requestedAmount: paymentRequestData.requestedAmount,
      email: paymentRequestData.sendToEmail,
      shopRef: paymentRequestData.shopRef,
    })

    return {
      success: false,
      error: errorMessage,
    }
  }
}
